import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, Users, ArrowRightLeft, Clock, DollarSign, BookMarked, type LucideIcon } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import type { DashboardStats, MostIssuedBook, MonthlyTrend } from "@/types";

export default function DashboardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [mostIssued, setMostIssued] = useState<MostIssuedBook[]>([]);
  const [trends, setTrends] = useState<MonthlyTrend[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, mi, tr] = await Promise.all([api.get("/reports/dashboard"), api.get("/reports/most-issued"), api.get("/reports/monthly-trends")]);
        setStats(s.data);
        setMostIssued(mi.data);
        setTrends(tr.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api]);

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground">Loading dashboard...</div>;

  const statCards: Array<{ label: string; value: string | number; icon: LucideIcon; color: string; path: string }> = [
    { label: "Total Books", value: stats?.total_books || 0, icon: BookOpen, color: "#0f5c52", path: "/accession" },
    { label: "Issued Books", value: stats?.issued_books || 0, icon: ArrowRightLeft, color: "#f5a524", path: "/issue" },
    { label: "Overdue Books", value: stats?.overdue_books || 0, icon: Clock, color: "#f07178", path: "/reports" },
    { label: "Total Students", value: stats?.total_students || 0, icon: Users, color: "#1a9b8c", path: "/membership" },
    { label: "Fine Collected", value: `₹${stats?.fine_collected || 0}`, icon: DollarSign, color: "#2dd4a8", path: "/fines" },
    { label: "Reserved Books", value: stats?.reserved_books || 0, icon: BookMarked, color: "#9aa3b2", path: "/notifications" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Dashboard
        </h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        {statCards.map((card, i) => (
          <Card
            key={card.label}
            role="link"
            tabIndex={0}
            onClick={() => navigate(card.path)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(card.path);
              }
            }}
            className={`border-0 shadow-sm animate-fade-in stagger-${i + 1} cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
            data-testid={`stat-${card.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: card.color + "15" }}>
                  <card.icon className="w-5 h-5" style={{ color: card.color }} strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold tracking-tight">{card.value}</p>
              <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Issued Books */}
        <Card className="border-0 shadow-sm" data-testid="chart-most-issued">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              Most Issued Books
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mostIssued.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={mostIssued.slice(0, 7)} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="title" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0f5c52" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground text-sm">No issue data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Monthly Trends */}
        <Card className="border-0 shadow-sm" data-testid="chart-monthly-trends">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              Monthly Issue Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="issues" stroke="#f5a524" strokeWidth={2.5} dot={{ fill: "#f5a524", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground text-sm">No trend data yet</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
